import { useState, useEffect, useCallback } from "react";
import {
  Container,
  Center,
  Loader,
  Paper,
  Code,
  Text,
  Badge,
  NumberFormatter,
  Group,
  Button,
  Checkbox,
  Table,
  Tooltip,
  Modal,
  Stack,
} from "@mantine/core";
import {
  getAdminJobDetails,
  requeueJob,
  requeueJobHard,
  cancelJob,
  failJob,
} from "../api";
import JobStatusBadge from "./JobStatusBadge";
import { Link } from "wouter";
import { notifications } from "@mantine/notifications";
import React from "react";

// Matched by prefix so every spelling the backend has ever used lands on the right badge:
// "warn"/"Warning"/"warning" are all the same level, and a level we don't know still gets a
// readable grey badge rather than a mystery.
const LOG_LEVELS = [
  { prefix: "err", label: "Error", color: "red" },
  { prefix: "crit", label: "Critical", color: "red" },
  { prefix: "fatal", label: "Critical", color: "red" },
  { prefix: "warn", label: "Warning", color: "yellow" },
  { prefix: "info", label: "Info", color: "blue" },
  { prefix: "debug", label: "Debug", color: "gray" },
  { prefix: "trace", label: "Trace", color: "gray" },
];

function LogLevelBadge({ size = "md", level }) {
  const normalized = String(level ?? "").toLowerCase();
  const match = LOG_LEVELS.find((l) => normalized.startsWith(l.prefix));

  return (
    // flex 0 0 auto: the badge shares a fixed-width cell with the timestamp, and without this
    // flexbox shrinks it until the label is cut down to "W…".
    <Badge size={size} variant="light" color={match?.color ?? "gray"} style={{ flex: "0 0 auto" }}>
      {match?.label ?? level}
    </Badge>
  );
}

// "2026-07-27T13:51:36.6442Z" → ["2026-07-27", "13:51:36.644"]. The date is only worth a line of
// its own when it changes, which for most jobs means once.
function splitTimestamp(timestamp) {
  const iso = String(timestamp ?? "");
  const separator = iso.indexOf("T");
  if (separator < 0) return [iso, ""];
  return [iso.slice(0, separator), iso.slice(separator + 1, separator + 13)];
}

function JobLogDetails({ logs }) {
  return (
    <Table pl="md" mb="xl">
      <Table.Thead>
        <Table.Tr>
          <Table.Th>Timestamp</Table.Th>
          <Table.Th>Machine</Table.Th>
          <Table.Th>Duration</Table.Th>
          <Table.Th>Status</Table.Th>
        </Table.Tr>
      </Table.Thead>
      <Table.Tbody>
        {logs?.map((log) => (
          <>
            <Table.Tr>
              <Table.Td>{log.idCreated}</Table.Td>
              <Table.Td>{log.machineName}</Table.Td>
              <Table.Td>
                <NumberFormatter
                  thousandSeparator
                  value={log.milliseconds}
                  suffix="ms"
                />
              </Table.Td>
              <Table.Td>
                <JobStatusBadge variant="light" status={log.status} />
              </Table.Td>
            </Table.Tr>
            {log.state && (
              <Table.Tr mt="md" mb="md">
                <Table.Td colSpan={4}>
                  <Text mb="xs">State</Text>
                  <Code block style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>{JSON.stringify(log.state, null, 2)}</Code>
                </Table.Td>
              </Table.Tr>
            )}
          </>
        ))}
      </Table.Tbody>
    </Table>
  );
}

const TERMINAL_STATUSES = ["success", "failed"];

export default function JobDetails({ params }) {
  const [job, setJob] = useState(null);
  const [logs, setLogs] = useState([]);
  const [logEntries, setLogEntries] = useState([]);
  const [jobType, setJobType] = useState(null);

  const [isLoading, setIsLoading] = useState(false);
  const [autoReload, setAutoReload] = useState(true); // default enabled
  const [failConfirmOpen, setFailConfirmOpen] = useState(false);

  const RELOAD_INTERVAL_MS = 15000; // 15s

  const loadData = useCallback(() => {
    setIsLoading(true);
    getAdminJobDetails(params.id)
      .then((data) => {
        setJob(data.job);
        setLogs(data.logs);
        setJobType(data.type);
        setLogEntries(data.logEntries.toReversed() || []);
      })
      .finally(() => setIsLoading(false));
  }, [params.id]);

  useEffect(() => {
    loadData();
  }, [params.id, loadData]);

  useEffect(() => {
    if (!autoReload) return;
    const interval = setInterval(() => {
      loadData();
    }, RELOAD_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [autoReload, loadData, RELOAD_INTERVAL_MS]);

  const runAction = (action, successTitle, errorTitle) => {
    setIsLoading(true);
    action(job.id)
      .then(() => {
        notifications.show({ title: successTitle, color: "green" });
        loadData();
      })
      .catch((error) => {
        notifications.show({
          title: errorTitle,
          message: "" + error,
          color: "red",
        });
        setIsLoading(false);
      });
  };

  if (!job)
    return (
      <Center mt="xl">
        <Loader size="xl" />
      </Center>
    );

  const isTerminal = TERMINAL_STATUSES.includes(job.status);

  return (
    <Container fluid px={0}>
      <Paper p="md" radius="md" withBorder>
        {/* <Code block>{JSON.stringify(job, null, 2)}</Code> */}
        <Group justify="space-between" align="flex-start">
          <Group>
            <Text fw={500} fz="lg">
              {job.id} ({jobType})
            </Text>
            <Text>{job.originalFileName}</Text>
            {isLoading && <Loader size="sm" />}
          </Group>
          <Group gap="md">
            <Checkbox
              size="sm"
              label="Auto-refresh (15s)"
              checked={autoReload}
              onChange={(e) => setAutoReload(e.currentTarget.checked)}
            />
            <Button
              size="xs"
              variant="light"
              onClick={loadData}
              disabled={isLoading}
            >
              Refresh
            </Button>
          </Group>
        </Group>
        <Group>
          <JobStatusBadge status={job.status} />
          {/* A retry count in the hundreds is the tell for a job that re-queues itself. */}
          <Tooltip label="How many times this job has been locked and run">
            <Badge variant="light" color={job.retryCount > 50 ? "red" : "gray"}>
              {job.retryCount} runs
            </Badge>
          </Tooltip>
          {job.cancellationRequested && (
            <Badge variant="light" color="orange">
              cancellation requested
            </Badge>
          )}
          <Text c="dimmed">First started at: {job.firstStarted}</Text>
        </Group>
        <Group justify="flex-end">
          <Tooltip
            multiline
            w={340}
            label="Asks a running job to stop at its next checkpoint. Cooperative only: the job keeps its status, so a handler that ignores cancellation keeps going, and a queued job still gets picked up."
          >
            <Button
              size="sm"
              my="md"
              variant="default"
              disabled={isLoading || isTerminal}
              onClick={() =>
                runAction(
                  cancelJob,
                  "Cancellation requested",
                  "Failed to request cancellation"
                )
              }
            >
              Request Cancellation
            </Button>
          </Tooltip>

          <Tooltip
            multiline
            w={340}
            label="Kill switch. Marks the job Failed and takes it out of the queue, then lets its handler clean up. This is what stops a job that re-queues itself over and over."
          >
            <Button
              size="sm"
              my="md"
              color="red"
              disabled={isLoading || isTerminal}
              onClick={() => setFailConfirmOpen(true)}
            >
              Fail Job Now
            </Button>
          </Tooltip>

          <Button
            size="sm"
            my="md"
            color="blue"
            disabled={isLoading}
            onClick={() =>
              runAction(requeueJob, "Job re-queued", "Failed to re-queue job")
            }
          >
            Re-Queue Job
          </Button>

          <Tooltip
            multiline
            w={340}
            label="Re-queues the job, setting its retry count and original start to null. It also resets progress, cancellation requests etc. It does NOT reset the job's internal state, however."
          >
            <Button
              size="sm"
              my="md"
              color="red"
              variant="outline"
              disabled={isLoading}
              onClick={() =>
                runAction(
                  requeueJobHard,
                  "Job re-queued HARD",
                  "Failed to re-queue job"
                )
              }
            >
              Re-Queue Job HARD
            </Button>
          </Tooltip>
        </Group>

        <Modal
          opened={failConfirmOpen}
          onClose={() => setFailConfirmOpen(false)}
          title="Fail this job?"
          centered
        >
          <Stack>
            <Text size="sm">
              This marks <Code>{job.id}</Code> ({jobType}) as failed and removes
              it from the queue, so the scheduler stops running it. Its handler
              gets a chance to clean up.
            </Text>
            <Text size="sm" c="dimmed">
              Work already done is not rolled back. You can put the job back with
              Re-Queue afterwards.
            </Text>
            <Group justify="flex-end">
              <Button
                variant="default"
                onClick={() => setFailConfirmOpen(false)}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={() => {
                  setFailConfirmOpen(false);
                  runAction(failJob, "Job failed", "Failed to stop job");
                }}
              >
                Fail Job
              </Button>
            </Group>
          </Stack>
        </Modal>

        {job.documentFileId && (
          <Paper my="md">
            Processed file:{" "}
            <Link to={`/documents/${job.documentFileId}`}>
              {job.documentFileId}
            </Link>
          </Paper>
        )}

        {job.tenderId && (
          <Paper my="md">
            Processed tender:{" "}
            <Link to={`/tenders/${job.tenderId}`}>{job.tenderId}</Link>
          </Paper>
        )}

        <Table style={{ tableLayout: "fixed" }}>
          <Table.Thead>
            <Table.Tr>
              <Table.Th style={{ width: "230px" }}>Timestamp</Table.Th>
              <Table.Th>Message</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {logEntries?.map((entry, index) => {
              const [date, time] = splitTimestamp(entry.timestamp);
              const [previousDate] = splitTimestamp(logEntries[index - 1]?.timestamp);

              return (
                <React.Fragment key={entry.id}>
                  <Table.Tr>
                    <Table.Td style={{ width: "230px", verticalAlign: "top" }}>
                      <Group gap="xs" wrap="nowrap" align="center">
                        <LogLevelBadge size="xs" level={entry.level} />
                        <Text ff="monospace" fz="xs" c="dimmed" title={entry.timestamp}
                              style={{ whiteSpace: "nowrap" }}>
                          {time}
                        </Text>
                      </Group>
                      {date !== previousDate && (
                        <Text ff="monospace" fz={9} c="dimmed">
                          {date}
                        </Text>
                      )}
                      <Text truncate="start" fz={9} c="dimmed">
                        {entry.name}
                      </Text>
                    </Table.Td>
                    <Table.Td style={{ wordBreak: "break-word" }}>
                      <Text fz="xs" style={{ whiteSpace: "pre-wrap" }}>{entry.message}</Text>
                    </Table.Td>
                  </Table.Tr>
                  {entry.exception && (
                    <Table.Tr>
                      <Table.Td colSpan={2}>
                        <Code
                          block
                          style={{
                            fontSize: "11px",
                            textWrap: "auto",
                          }}
                        >
                          {entry.exception}
                        </Code>
                      </Table.Td>
                    </Table.Tr>
                  )}
                </React.Fragment>
              );
            })}
          </Table.Tbody>
        </Table>

        <Text mt="lg" fw={500} fz="lg">
          Executions
        </Text>
        <JobLogDetails logs={logs} />
      </Paper>

      <Text mt={100}>Full job JSON</Text>
      <Code mt="sm" block style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
        {JSON.stringify(job, null, 2)}
      </Code>
    </Container>
  );
}
